import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";

/**
 * Pill promo: app icon + name (+ optional tagline) + store badge(s) in one row.
 * `variant`: "light" (cards) | "hero" (frosted glass on colored bands).
 */
export default function AppStorePromoPill({
    iconSrc,
    appName,
    tagline,
    children,
    variant = "light",
    sx = {},
}) {
    const isHero = variant === "hero";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 1.25,
                width: "auto",
                maxWidth: "100%",
                px: 1.5,
                py: 1,
                borderRadius: 999,
                boxSizing: "border-box",
                ...(isHero
                    ? {
                          bgcolor: "rgba(15, 23, 42, 0.32)",
                          border: "1px solid rgba(255,255,255,0.28)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                          backdropFilter: "blur(12px)",
                          WebkitBackdropFilter: "blur(12px)",
                      }
                    : {
                          bgcolor: "rgba(236, 242, 236, 0.95)",
                          border: "1px solid rgba(46, 125, 50, 0.14)",
                          boxShadow: "0 2px 10px rgba(27, 94, 32, 0.08)",
                      }),
                ...sx,
            }}
        >
            <Box
                component="img"
                src={iconSrc}
                alt={appName}
                title={appName}
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "10px",
                    objectFit: "cover",
                    flexShrink: 0,
                    boxShadow: isHero
                        ? "0 2px 10px rgba(0,0,0,0.28)"
                        : "0 2px 8px rgba(15, 23, 42, 0.16)",
                    border: isHero ? "1px solid rgba(255,255,255,0.35)" : "none",
                }}
            />
            <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                <Typography
                    component="div"
                    sx={{
                        m: 0,
                        fontWeight: 800,
                        fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                        letterSpacing: "0.03em",
                        textTransform: "uppercase",
                        color: isHero ? "#F8FAFC" : "#1f2937",
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        textShadow: isHero ? "0 1px 2px rgba(0,0,0,0.35)" : "none",
                    }}
                >
                    {appName}
                </Typography>
                {tagline ? (
                    <Typography
                        component="div"
                        sx={{
                            m: 0,
                            mt: 0.35,
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            color: isHero ? "rgba(248,250,252,0.82)" : "#6B7280",
                            lineHeight: 1.35,
                            maxWidth: { xs: 200, sm: 280 },
                        }}
                    >
                        {tagline}
                    </Typography>
                ) : null}
            </Box>
            {children ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        flexShrink: 0,
                    }}
                >
                    {children}
                </Box>
            ) : null}
        </Box>
    );
}

AppStorePromoPill.propTypes = {
    iconSrc: PropTypes.string.isRequired,
    appName: PropTypes.string.isRequired,
    tagline: PropTypes.string,
    children: PropTypes.node,
    variant: PropTypes.oneOf(["light", "hero"]),
    sx: PropTypes.object,
};
